import React, { ReactNode } from 'react';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { type MonitorEvent } from '@/lib/db/schema';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getUptimePercentage(events: MonitorEvent[]): number {
  if (events.length == 0) {
    return -1;
  }

  const sorted = [...events].sort(
    (a, b) => new Date(a.checkedAt).getTime() - new Date(b.checkedAt).getTime()
  );

  let downtimeMs = 0;

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    const interval = new Date(next.checkedAt).getTime() - new Date(current.checkedAt).getTime();

    if (current.statusCategory === 'DOWN') {
      downtimeMs += interval;
    }
  }

  const totalMs =
    new Date(sorted[sorted.length - 1].checkedAt).getTime() -
    new Date(sorted[0].checkedAt).getTime();

  const uptimeRatio = totalMs > 0 ? (totalMs - downtimeMs) / totalMs : 1;
  let uptimePercent = uptimeRatio * 100;

  // Manually force status/uptime if there's only 1 monitor event.
  if (sorted.length == 1) {
    uptimePercent = sorted[0].statusCategory === 'DOWN' ? 0 : 100;
  }

  return uptimePercent;
}

export function UptimePercentage({ uptimePercent }: { uptimePercent: number }): ReactNode {
  const formattedUptimePercent =
    uptimePercent < 0
      ? 'No data'
      : uptimePercent.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 3,
        }) + '%';

  const uptimePercentColorClass = getUptimeColorClasses(uptimePercent).text;

  return <span className={uptimePercentColorClass}>{formattedUptimePercent}</span>;
}

export function getUptimeColorClasses(uptimePercent: number) {
  if (uptimePercent < 0) {
    return {
      text: 'text-no-status',
      bg: 'bg-no-status',
    };
  }

  if (uptimePercent >= 99.9) {
    return {
      text: 'text-healthy',
      bg: 'bg-healthy',
    };
  }

  if (uptimePercent > 99) {
    return {
      text: 'text-warning',
      bg: 'bg-warning',
    };
  }

  if (uptimePercent >= 0) {
    return {
      text: 'text-critical',
      bg: 'bg-critical',
    };
  }

  return {
    text: 'text-no-status',
    bg: 'bg-no-status',
  };
}

export function chunkArray<T>(items: T[], size: number) {
  if (!Number.isFinite(size) || size <= 0) {
    throw new Error('chunk size must be a positive number');
  }

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
}
